import { Button } from '@/components/ui/button'
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area'
import { useFetchRelayInfo } from '@/hooks/useFetchRelayInfo'
import { cn } from '@/lib/utils'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  CheckIcon,
  Copy01Icon,
  GitBranchIcon,
  Link01Icon,
  Mail01Icon,
  CodeSquareIcon
} from '@hugeicons/core-free-icons'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import PostEditor from './PostEditor'
import RelayIcon from './RelayIcon'
import RelayMembershipControl from './RelayMembershipControl'
import SaveRelayDropdownMenu from './SaveRelayDropdownMenu'
import UserAvatar from './UserAvatar'
import Username from './Username'
import { useAtomValue } from 'jotai'
import { canWriteAtom } from '@/atoms/session'

export default function RelayInfo({ url, className }: { url: string; className?: string }) {
  const { t } = useTranslation()
  const { relayInfo, isFetching } = useFetchRelayInfo(url)
  const [open, setOpen] = useState(false)
  const canWrite = useAtomValue(canWriteAtom)

  if (isFetching || !relayInfo) return null

  return (
    <div className={cn('space-y-4 mb-2', className)}>
      <div className="px-4 space-y-4">
        <div className="space-y-2">
          <div className="flex items-center gap-2 justify-between">
            <div className="flex gap-2 items-center truncate">
              <RelayIcon url={url} className="w-8 h-8" />
              <div className="text-2xl font-semibold truncate select-text">
                {relayInfo.name || relayInfo.urlSimple}
              </div>
            </div>
            <RelayControls url={relayInfo.url} />
          </div>
          {relayInfo.description && (
            <div className="text-wrap break-words whitespace-pre-wrap mt-2 select-text">
              {relayInfo.description}
            </div>
          )}
        </div>

        <div className="space-y-2">
          <div className="text-sm font-semibold text-muted-foreground">{t('Homepage')}</div>
          <a
            href={relayInfo.url.replace('wss://', 'https://').replace('ws://', 'http://')}
            target="_blank"
            className="hover:underline text-primary select-text truncate block w-fit max-w-full"
          >
            {relayInfo.url.replace('wss://', 'https://').replace('ws://', 'http://')}
          </a>
        </div>

        <ScrollArea className="overflow-x-auto">
          <div className="flex gap-8 pb-2">
            {relayInfo.pubkey && (
              <div className="space-y-2 w-fit">
                <div className="text-sm font-semibold text-muted-foreground">{t('Operator')}</div>
                <div className="flex gap-2 items-center">
                  <UserAvatar user={relayInfo.pubkey} size="small" />
                  <Username user={relayInfo.pubkey} className="font-semibold text-nowrap" />
                </div>
              </div>
            )}
            {relayInfo.contact && (
              <div className="space-y-2 w-fit">
                <div className="text-sm font-semibold text-muted-foreground">{t('Contact')}</div>
                <div className="flex gap-2 items-center font-semibold select-text text-nowrap">
                  <HugeiconsIcon icon={Mail01Icon} />
                  {relayInfo.contact}
                </div>
              </div>
            )}
            {relayInfo.software && (
              <div className="space-y-2 w-fit">
                <div className="text-sm font-semibold text-muted-foreground">{t('Software')}</div>
                <div className="flex gap-2 items-center font-semibold select-text text-nowrap">
                  <HugeiconsIcon icon={CodeSquareIcon} />
                  {formatSoftware(relayInfo.software)}
                  {relayInfo.version && relayInfo.version !== 'n/a' && (
                    <>
                      <HugeiconsIcon icon={GitBranchIcon} />
                      {relayInfo.version}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
        <RelayMembershipControl relayInfo={relayInfo} />
        {canWrite && (
          <Button variant="secondary" className="w-full" onClick={() => setOpen(true)}>
            {t('Write on this relay')}
          </Button>
        )}
        <PostEditor open={open} setOpen={setOpen} openFromRelays={[relayInfo.url]} />
      </div>
    </div>
  )
}

function formatSoftware(software: string) {
  const parts = software.split('/')
  return parts[parts.length - 1]
}

function RelayControls({ url }: { url: string }) {
  const [copiedUrl, setCopiedUrl] = useState(false)
  const [copiedShareableUrl, setCopiedShareableUrl] = useState(false)
  const canWrite = useAtomValue(canWriteAtom)

  return (
    <div className="flex items-center gap-1">
      <Button variant="ghost" size="titlebar-icon" onClick={handleCopyShareableUrl}>
        {copiedShareableUrl ? (
          <HugeiconsIcon icon={CheckIcon} />
        ) : (
          <HugeiconsIcon icon={Link01Icon} />
        )}
      </Button>
      <Button variant="ghost" size="titlebar-icon" onClick={handleCopyUrl}>
        {copiedUrl ? <HugeiconsIcon icon={CheckIcon} /> : <HugeiconsIcon icon={Copy01Icon} />}
      </Button>
      {canWrite && <SaveRelayDropdownMenu item={url} bigButton />}
    </div>
  )

  function handleCopyUrl() {
    navigator.clipboard.writeText(url)
    setCopiedUrl(true)
    setTimeout(() => setCopiedUrl(false), 2000)
  }

  function handleCopyShareableUrl() {
    navigator.clipboard.writeText(`${window.location.protocol}//${window.location.host}/?r=${url}`)
    setCopiedShareableUrl(true)
    toast.success('Shareable URL copied to clipboard')
    setTimeout(() => setCopiedShareableUrl(false), 2000)
  }
}
